import React, { useState, useEffect } from 'react';
import { Power, PowerOff } from 'lucide-react';
import { API_BASED_URL } from '../config.js';

const SUPER_ADMIN_TOKEN_KEY = 'superAdminToken';

const SuperAdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('up');
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(SUPER_ADMIN_TOKEN_KEY);
    if (token) setIsLoggedIn(true);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_BASED_URL}api/super-admin/status`);
        if (res.ok) {
          const data = await res.json();
          setStatus(data.status || 'up');
        }
      } catch {
        setStatus('up');
      }
    };
    fetchStatus();
  }, [isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASED_URL}api/super-admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem(SUPER_ADMIN_TOKEN_KEY, data.token);
        setStatus(data.status || 'up');
        setIsLoggedIn(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Invalid credentials.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(SUPER_ADMIN_TOKEN_KEY);
    setIsLoggedIn(false);
  };

  const handleToggle = async () => {
    const token = localStorage.getItem(SUPER_ADMIN_TOKEN_KEY);
    if (!token) return;
    setToggleLoading(true);
    try {
      const res = await fetch(`${API_BASED_URL}api/super-admin/toggle-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status || 'up');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.message || 'Toggle failed. Try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setToggleLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/80 backdrop-blur border border-slate-600/50 rounded-2xl shadow-2xl w-full max-w-sm p-8">
          <h1 className="text-xl font-bold text-center text-white mb-6 tracking-tight">
            Super Admin
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
          </form>
        </div>
      </div>
    );
  }

  const isDown = status === 'down';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/80 backdrop-blur border border-slate-600/50 rounded-2xl shadow-2xl w-full max-w-md p-8">
        <h1 className="text-xl font-bold text-center text-white mb-2">Super Admin</h1>
        <p className="text-slate-400 text-sm text-center mb-8">Site control</p>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-4 px-4 bg-slate-700/40 rounded-xl">
            <div>
              <p className="text-slate-300 text-sm">Website status</p>
              <p className={`text-lg font-semibold ${isDown ? 'text-amber-400' : 'text-emerald-400'}`}>
                {isDown ? 'Down (maintenance)' : 'Live'}
              </p>
            </div>
            <button
              onClick={handleToggle}
              disabled={toggleLoading}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition disabled:opacity-50 ${
                isDown
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-amber-600 hover:bg-amber-500 text-white'
              }`}
            >
              {toggleLoading ? (
                'Please wait...'
              ) : isDown ? (
                <>
                  <Power size={18} /> Bring Up
                </>
              ) : (
                <>
                  <PowerOff size={18} /> Take Down
                </>
              )}
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="w-full py-2 text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
